import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/permissions";
import { PageHeader, StatusBadge } from "@/components/common/Primitives";
import { DataTable } from "@/components/common/DataTable";
import { ContractForm } from "@/components/forms/ContractForm";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { CONTRACT_STATUS_MAP, CATEGORY_LABELS } from "@/lib/constants";
import { fmtEUR, fmtDate } from "@/lib/format";
import { Plus, Search } from "lucide-react";

export default function Contracts() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [category, setCategory] = useState("");
  const [formOpen, setFormOpen] = useState(false);

  useEffect(() => { if (params.get("neu")) { setFormOpen(true); setParams({}); } }, [params, setParams]);

  const { data: reg } = useQuery({ queryKey: ["registry"], queryFn: async () => (await api.get("/settings/registry")).data });
  const { data, isLoading } = useQuery({
    queryKey: ["contracts", q, status, category],
    queryFn: async () => (await api.get(`/contracts?q=${encodeURIComponent(q)}&status=${status}&category=${category}`)).data,
  });

  const daysCls = (d) => {
    if (d == null) return "text-muted-foreground";
    if (d <= 30) return "font-semibold text-destructive";
    if (d <= 60) return "font-semibold text-amber-600 dark:text-amber-400";
    return "text-muted-foreground";
  };

  const columns = [
    { key: "contract", header: "Vertrag", render: (c) => (
      <div className="min-w-0"><div className="truncate font-medium">{c.provider} · {c.tariff_name}</div>
        <div className="truncate text-xs text-muted-foreground font-mono">{c.contract_number}</div></div>
    ) },
    { key: "customer_name", header: "Kunde", render: (c) => c.customer_name || "—" },
    { key: "category", header: "Kategorie", mobileHidden: true, render: (c) => CATEGORY_LABELS[c.category] || c.category },
    { key: "monthly_fee", header: "Gebühr", render: (c) => fmtEUR(c.monthly_fee) },
    { key: "end_date", header: "Ende", render: (c) => (
      <div className="flex items-center gap-2"><span>{fmtDate(c.end_date)}</span>
        {c.days_to_end != null && <span className={daysCls(c.days_to_end)}>{c.days_to_end}T</span>}</div>
    ) },
    { key: "status", header: "Status", render: (c) => <StatusBadge status={c.status} map={CONTRACT_STATUS_MAP} /> },
  ];

  return (
    <div>
      <PageHeader title="Verträge" description={`${data?.total ?? 0} Verträge`}
        actions={can(user?.role, "contracts:write") && (
          <Button className="gap-1.5 bg-accent text-accent-foreground hover:bg-accent/90" onClick={() => setFormOpen(true)} data-testid="new-contract-button">
            <Plus className="h-4 w-4" /> Neuer Vertrag
          </Button>
        )} />

      <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
        <div className="relative flex-1 sm:max-w-xs">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Anbieter, Tarif, Nr., Kunde…" className="pl-9" data-testid="contract-search-input" />
        </div>
        <Select value={status || "all"} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
          <SelectTrigger className="sm:w-48" data-testid="contract-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Status</SelectItem>
            {Object.entries(CONTRACT_STATUS_MAP).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={category || "all"} onValueChange={(v) => setCategory(v === "all" ? "" : v)}>
          <SelectTrigger className="sm:w-44" data-testid="contract-category-filter"><SelectValue placeholder="Kategorie" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Kategorien</SelectItem>
            {Object.entries(CATEGORY_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <DataTable columns={columns} rows={data?.items} loading={isLoading}
        onRowClick={(c) => navigate(`/vertraege/${c.id}`)} testIdPrefix="contract-row" />

      <ContractForm open={formOpen} onOpenChange={setFormOpen} registry={reg}
        onSaved={() => qc.invalidateQueries({ queryKey: ["contracts"] })} />
    </div>
  );
}
